import digitalData from 'digital-data';
import isEmpty from 'lodash/isEmpty';
import CONSTANTS from './constants';

const {
  AB_TEST,
  PAGE,
  PAGE_INFO,
  PAGE_TYPE,
  PAGE_NAME,
  CATEGORY,
  PRIMARY_CATEGORY,
  SUB_CATEGORY1,
  ERROR,
  EVENT_NAME,
  EVENT_INFO,
  EVENT_MODE,
  SETTINGS,
  MY_ACCOUNT,
  STASH,
} = CONSTANTS;

/**
 * Analytics - Helps to set the data required by global DDO (digitalData)
 *
 * Class helps to set the bread crumb information into the DDO. You can also pass the
 * values directly, if you don't want the data to come from breadCrumb
 *
 * Also, you can push the <IAnalyticsEvent> event into the digitalData event object
 *
 */

/**
 *  Send Page Informations to DDO
 */
export function setPageInfo(
  pageInfo: Object,
  eventInfo: Object,
  customInfo: Object
) {
  try {
    // Set the page object to digitalData object
    const DDO = digitalData.get();
    if (DDO != null) {
      DDO[PAGE] = {};
      DDO[PAGE][PAGE_INFO] = Object.assign(
        { [AB_TEST]: 'orders:version:react' },
        pageInfo
      );
      if (!isEmpty(customInfo)) {
        DDO[PAGE] = Object.assign(DDO[PAGE], customInfo);
      }
      DDO[PAGE][EVENT_INFO] = eventInfo;
      digitalData.page = DDO[PAGE];
      digitalData.init();
    }
  } catch (err) {
    // Suppressing the DDO error, so that functionality is not affected
    console.error(
      'DDO : Error in setting the page object and initializing it!!!'
    );
  }
}

export function setPageInfoWithCustom(
  pageInfo: Object,
  customInfo: Object = {}
) {
  try {
    // Set the page object to digitalData object
    const DDO = digitalData.get();
    if (DDO != null) {
      DDO[PAGE] = {};
      DDO[PAGE][PAGE_INFO] = Object.assign(
        { [AB_TEST]: 'orders:version:react' },
        pageInfo
      );
      if (!isEmpty(customInfo)) {
        DDO[PAGE].myAccount = customInfo;
      }
      digitalData.page = DDO[PAGE];
      digitalData.init();
    }
  } catch (err) {
    // Suppressing the DDO error, so that functionality is not affected
    console.error(
      'DDO : Error in setting the page object and initializing it!!!'
    );
  }
}

/**
 * Helps to add custom page data
 */
export function addCustomInfo(customTitle: string, customData: any) {
  try {
    const DDO = digitalData.get();
    if (DDO != null) {
      DDO[customTitle] = Object.assign({}, customData);
    }
  } catch (err) {
    // Suppressing the DDO error, so that functionality is not affected
    console.error('DDO : Error in adding custom page objects!!!');
  }
}

export function addCustomPageInfo(customTitle: string, customData: any) {
  try {
    const DDO = digitalData.get();
    if (DDO != null) {
      DDO[PAGE] = DDO[PAGE] || {};
      DDO[PAGE][customTitle] = Object.assign({}, customData);
    }
  } catch (err) {
    // Suppressing the DDO error, so that functionality is not affected
    console.error('DDO : Error in adding custom page objects!!!');
  }
}

/**
 * Push the event info to the DDO object
 */
export function pushEventInfo(analyticsEvent: Object) {
  try {
    const DDO = digitalData.get();
    if (analyticsEvent && DDO != null) {
      DDO.event.push(analyticsEvent);
    }
  } catch (err) {
    // Suppressing the DDO error, so that functionality is not affected
    console.error('DDO : Error in pushing the event info!!!');
  }
}

/**
 * Send Page Informations to DDO
 *
 * Method gets the data from bread crumb and set the page information
 *
 */
export function setBreadCrumbPageInfo(
  pageType: string,
  pageTitle: string,
  breadCrumbArray: Array
) {
  try {
    // Validating the inputs
    if (pageType && pageTitle && breadCrumbArray) {
      // Setting the page info to add the custom data from the consumers
      const breadCrumb = breadCrumbArray
        .map((elem: Object) => elem.title)
        .join('>')
        .toLowerCase();

      const pageName = `${breadCrumb}>${pageTitle.toLowerCase()}`;
      const pageNameArray = pageName.split('>');
      const subCategory1 = `${pageNameArray[0]}>${pageNameArray[1]}`;

      setPageInfo(
        {
          [PAGE_TYPE]: pageType,
          [PAGE_NAME]: pageName,
        },
        {
          [PRIMARY_CATEGORY]: 'my account',
          [SUB_CATEGORY1]: subCategory1,
          [PAGE_NAME]: pageName,
        }
      );
    }
  } catch (err) {
    // Suppressing the DDO error, so that functionality is not affected
    console.error('DDO : Not able construct the page object information!!!');
  }
}

/**
 * Push simple event with just event name and primary category.
 * @param {string} eventName
 * @param {string} eventCategory
 */
export function pushEventNameAndCategory(
  eventName: string,
  eventCategory: string
) {
  pushEventInfo({
    [EVENT_INFO]: { [EVENT_NAME]: eventName },
    [CATEGORY]: { [PRIMARY_CATEGORY]: eventCategory },
  });
}

/**
 * Push simple event with just event name, primary category, and stash.
 * @param {string} eventName
 * @param {string} eventCategory
 */
export function pushStashEventInfo(
  eventName: string,
  eventCategory: string,
  stash: Object
) {
  pushEventInfo({
    [EVENT_INFO]: { [EVENT_NAME]: eventName },
    [CATEGORY]: { [PRIMARY_CATEGORY]: eventCategory },
    [EVENT_MODE]: 'stash',
    [STASH]: stash,
  });
}
/**
 * Push simple event with event name, category, and setting.
 * @param {string} eventName
 * @param {string} eventCategory
 * @param {string} eventSetting
 */
export function pushEventNameCategorySetting(
  eventName: string,
  eventCategory: string,
  eventSetting: string
) {
  pushEventInfo({
    [EVENT_INFO]: { [EVENT_NAME]: eventName },
    [CATEGORY]: { [PRIMARY_CATEGORY]: eventCategory },
    [MY_ACCOUNT]: { [SETTINGS]: eventSetting },
  });
}

/**
 * Error information when we are unable to load the component
 */
export function pushErrorEvent(error: any) {
  pushEventInfo({
    [CATEGORY]: { [PRIMARY_CATEGORY]: 'error' },
    [EVENT_INFO]: { [EVENT_NAME]: 'post page error', [ERROR]: [error] },
  });
}

/**
 *  Send Page Informations to DDO
 */
export function setErrorPageInfo(pageInfo: array) {
  try {
    // Set the page object to digitalData object
    const DDO = digitalData.get();
    if (DDO != null) {
      DDO[PAGE] = {};
      DDO[PAGE][PAGE_INFO] = { [AB_TEST]: 'orders:version:react' };
      DDO[PAGE][ERROR] = pageInfo;
      digitalData.page = DDO[PAGE];
      digitalData.init();
    }
  } catch (err) {
    // Suppressing the DDO error, so that functionality is not affected
    console.error(
      'DDO : Error in setting the page object and initializing it!!!'
    );
  }
}
